import React, { useState } from "react";
import { ChevronDown, ChevronRight, Loader, ShieldQuestion } from "lucide-react";
import { useConsumerHouseManualReview } from "../hooks/useTreasury";
import { useCollapsed } from "../hooks/useCollapsed";
import { formatCurrencyAmount } from "../utils/formatCurrencyAmount";
import ReconciliationRecords from "./ReconciliationRecords";

const signed = (value, currency) => {
  const num = Number(value);
  if (Math.abs(num) < 0.005) return formatCurrencyAmount(0, currency);
  return `${num > 0 ? "+" : "-"}${formatCurrencyAmount(Math.abs(num), currency)}`;
};

const usd = (value) => (value === null || value === undefined ? null : formatCurrencyAmount(value, "USD"));

const Th = ({ children, title, right = true }) => (
  <th title={title} className={`px-3 py-2 text-xs font-bold tracking-wider text-gray-500 uppercase ${right ? "text-right" : "text-left"}`}>
    {children}
  </th>
);

// A figure in its own currency with the dollar value under it.
const Money = ({ value, currency, dollars }) => (
  <div className="text-right">
    <div className="text-gray-800">{formatCurrencyAmount(value, currency)}</div>
    <div className="text-xs text-gray-400">{currency === "USD" ? "" : dollars === null || dollars === undefined ? "no USD rate" : `≈ ${usd(dollars)}`}</div>
  </div>
);

const Tile = ({ label, value, hint, tone = "text-gray-900" }) => (
  <div className="p-3 bg-white rounded border border-gray-200" title={hint}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-lg font-semibold ${tone}`}>{value}</p>
  </div>
);

// Deposits waiting in manual review, kept apart from the position above because
// no balance holds them: not the customer's yet and, for a Septly card deposit,
// already the house merchant's. Approving them makes the customer side jump with
// nothing to match, so each currency says whether the house merchant balance
// still covers customers once they are released.
function ManualReviewSection() {
  const { data, isLoading, isError, error } = useConsumerHouseManualReview();
  const { collapsed, toggle } = useCollapsed("treasury-manual-review");
  const [open, setOpen] = useState(null); // currency whose records are shown

  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const totalCount = rows.reduce((sum, r) => sum + r.held.count, 0);

  return (
    <div className={`mb-4 rounded border border-amber-200 bg-amber-50/40 ${collapsed ? "p-3" : "p-4"}`}>
      <button onClick={toggle} className="flex gap-2 items-center text-sm font-bold text-[#013941]" aria-expanded={!collapsed}>
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        <ShieldQuestion className="w-4 h-4" />
        Held for manual review
        {!isLoading && totalCount > 0 && (
          <span className="px-2 py-0.5 text-xs font-semibold text-amber-800 bg-amber-100 rounded-full">
            {totalCount} deposit{totalCount === 1 ? "" : "s"}
          </span>
        )}
        {collapsed && totals?.notCovered?.length > 0 && (
          <span className="px-2 py-0.5 text-xs font-semibold text-red-800 bg-red-100 rounded-full">
            {totals.notCovered.length} not covered after release
          </span>
        )}
      </button>

      {!collapsed && (
        <div className="mt-3">
          <p className="mb-3 max-w-3xl text-xs text-gray-600">
            No balance holds a deposit that is waiting for review. A card deposit through Septly credits the house merchant the moment
            the payment succeeds, but the customer only when it is approved. So releasing one makes what customers are owed jump
            with nothing to match. For each currency, "after release" is today's position with every deposit below approved: does the
            house merchant balance still cover what customers would be owed?
          </p>
          {isLoading ? (
            <p className="flex gap-2 items-center text-sm text-gray-500">
              <Loader className="w-4 h-4 animate-spin" /> Loading held deposits...
            </p>
          ) : isError ? (
            <p className="text-sm text-red-600">Could not load the held deposits: {error?.message}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500">Nothing is held for manual review.</p>
          ) : (
            <>
              <div className="grid gap-2 mb-3 sm:grid-cols-4">
                <Tile label="Total held (USD)" value={usd(totals.depositedUsd)} hint="What the senders paid, across all currencies, in dollars" />
                <Tile
                  label="Customers would be owed (USD)"
                  value={usd(totals.estimatedOwedUsd)}
                  hint="Deposit less the fees recorded so far. The reserve counts as owed; an OTC fee decided at approval is not known yet."
                />
                <Tile
                  label="Gap after release (USD)"
                  value={`${totals.gapAfterReleaseUsd < 0 ? "-" : totals.gapAfterReleaseUsd > 0 ? "+" : ""}${usd(Math.abs(totals.gapAfterReleaseUsd))}`}
                  tone={totals.gapAfterReleaseUsd < 0 ? "text-red-600" : "text-green-700"}
                  hint="House merchant minus consumers, over all currencies priced in dollars, if every held deposit were approved today"
                />
                <Tile
                  label="Not covered after release"
                  value={totals.notCovered.length === 0 ? "None" : totals.notCovered.join(", ")}
                  tone={totals.notCovered.length === 0 ? "text-green-700" : "text-red-600"}
                  hint="Currencies where the house merchant balance would be below what customers are owed"
                />
              </div>
              {totals.gapAfterReleaseUsd >= 0 && totals.notCovered.length > 0 && (
                <p className="mb-2 text-xs text-gray-600">
                  Portfolio view only: a positive dollar total does not cover a currency that is short. Check each row.
                </p>
              )}
              {totals.unpriced.length > 0 && (
                <p className="mb-2 text-xs text-amber-700">
                  No USD rate for {totals.unpriced.join(", ")}: shown in their own currency and left out of the dollar totals.
                </p>
              )}
              <div className="overflow-x-auto bg-white rounded border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <Th right={false}>Currency</Th>
                      <Th title="Deposits waiting for review">Held</Th>
                      <Th title="What the senders paid">Deposited</Th>
                      <Th title="Deposit less the fees recorded so far">Customers would be owed</Th>
                      <Th title="What the house merchant has for these deposits">House merchant has</Th>
                      <Th title="House merchant balance minus consumers owed, now and with every held deposit released">Position now, after release</Th>
                      <Th right={false} title="Does the house merchant balance still cover customers once every held deposit is released?">
                        After release
                      </Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r) => {
                      const isOpen = open === r.currency;
                      const p = r.position;
                      return (
                        <React.Fragment key={r.currency}>
                          <tr className="align-top hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-900">{r.currency}</td>
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => setOpen(isOpen ? null : r.currency)}
                                title="Show the held deposits"
                                className="underline decoration-dotted underline-offset-2 hover:text-[#013941]"
                              >
                                {r.held.count}
                              </button>
                            </td>
                            <td className="px-3 py-2">
                              <Money value={r.held.deposited} currency={r.currency} dollars={r.usd.deposited} />
                            </td>
                            <td className="px-3 py-2">
                              <Money value={r.held.estimatedOwed} currency={r.currency} dollars={r.usd.estimatedOwed} />
                            </td>
                            <td className="px-3 py-2 text-xs text-right text-gray-700">
                              {r.houseAlreadyCredited.count > 0 && (
                                <div>already credited {formatCurrencyAmount(r.houseAlreadyCredited.amount, r.currency)} ({r.houseAlreadyCredited.count})</div>
                              )}
                              {r.houseHeldNotCredited.count > 0 && (
                                <div>held, not credited {formatCurrencyAmount(r.houseHeldNotCredited.amount, r.currency)} ({r.houseHeldNotCredited.count})</div>
                              )}
                              {r.noHouseRow.count > 0 && (
                                <div>no row {formatCurrencyAmount(r.noHouseRow.deposited, r.currency)} ({r.noHouseRow.count})</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <div className={p.gapNow < 0 ? "text-red-600" : "text-gray-700"}>{signed(p.gapNow, r.currency)}</div>
                              <div className={`font-semibold ${p.gapAfterRelease < -0.01 ? "text-red-600" : "text-green-700"}`}>
                                {signed(p.gapAfterRelease, r.currency)}
                              </div>
                              {r.currency !== "USD" && r.usd.gapAfterRelease !== null && (
                                <div className="text-xs text-gray-400">≈ {usd(r.usd.gapAfterRelease)}</div>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${p.covered ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                                title={`House merchant ${formatCurrencyAmount(p.houseHeld, r.currency)} plus ${formatCurrencyAmount(r.houseCreditStillToCome, r.currency)} still to be credited, against consumers owed ${formatCurrencyAmount(p.consumerOwed, r.currency)} plus ${formatCurrencyAmount(r.held.estimatedOwed, r.currency)}`}
                              >
                                {p.covered ? "Covered" : `Short by ${formatCurrencyAmount(Math.abs(p.gapAfterRelease), r.currency)}`}
                              </span>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={7} className="px-4 pb-3 bg-gray-50/70 border-t border-gray-100">
                                <ReconciliationRecords kind="manual_review_held" currency={r.currency} since={undefined} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ManualReviewSection;
